"use client";

import { motion } from "framer-motion";
import { GradientLine } from "@/app/components/ui/gradient-line";

const stats = [
  {
    label: "Years of Excellence",
    value: "10+",
    description:
      "A decade of transforming businesses through innovative technology solutions and strategic IT partnerships.",
  },
  {
    label: "Active Clients",
    value: "450+",
    description:
      "Supporting and empowering businesses from startups to enterprises across Ireland.",
  },
  {
    label: "Minutes Response Time",
    value: "15",
    description:
      "Industry-leading response time for critical issues, ensuring your business stays operational 24/7.",
  },
];

export const WhyChooseUs = () => {
  return (
    <section className="relative w-full bg-[rgb(var(--color-primary-dark)_/_0.95)] py-24 overflow-hidden">
      {/* Grid Pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.02) 1px, transparent 1px),
                           linear-gradient(to bottom, rgb(255 255 255 / 0.02) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      {/* Background Glow */}
      <motion.div
        className="absolute top-0 right-0 w-[600px] h-[600px] bg-[rgb(var(--color-neon-blue)_/_0.2)] rounded-full blur-[120px] -translate-y-1/4"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.2, 0.3, 0.2],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <div className="relative container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-6">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex px-4 py-2 rounded-full text-sm font-medium bg-[rgb(var(--color-neon-blue)_/_0.1)] text-[rgb(var(--color-neon-blue-light))] border border-[rgb(var(--color-neon-blue)_/_0.2)]"
          >
            YOUR TRUSTED IT PARTNER
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold text-white"
          >
            Why Choose Us?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto text-[rgb(var(--color-text-secondary))] text-lg leading-relaxed font-[450]"
          >
            Since 2015, we've been at the forefront of digital transformation,
            combining technical expertise with business acumen to deliver
            exceptional results. Our rapid response times, proactive support,
            and innovative solutions have made us the preferred technology
            partner for businesses seeking sustainable growth.
          </motion.p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * (index + 3) }}
              className="relative p-8 rounded-lg border border-[rgb(var(--color-neon-blue)_/_0.2)] bg-[rgb(var(--color-neon-blue)_/_0.03)] backdrop-blur-sm"
            >
              <div className="space-y-4">
                <span className="inline-block px-4 py-2 text-sm font-medium text-[rgb(var(--color-neon-blue-light))] bg-[rgb(var(--color-neon-blue)_/_0.1)] rounded-full">
                  {stat.label}
                </span>
                <div className="text-5xl font-bold text-white">
                  {stat.value}
                </div>
                <p className="text-[rgb(var(--color-text-secondary))] leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Bottom Left Gradient Line */}
      <div className="absolute bottom-0 -left-32 w-2/3">
        <GradientLine />
      </div>
    </section>
  );
};
